import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import InstallButton from "@/components/InstallButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowUpCircle, User, Users, Award, Receipt, LogOut, ShieldCheck, Settings } from "lucide-react";

const planLabel = (user) => {
  if (!user) return "";
  if (user.effective_plan === "premium") return "Summit Pass";
  if (user.plan === "trial") return `Trial — ${user.trial_days_remaining ?? 14}d left`;
  return "Day Hike";
};

const planBadgeClasses = (user) => {
  if (!user) return "bg-mm-navy/10 text-mm-navy";
  if (user.effective_plan === "premium") return "bg-mm-gold/25 text-mm-navy";
  if (user.plan === "trial") return "bg-mm-steel/20 text-mm-navy";
  return "bg-mm-navy/10 text-mm-navy";
};

const AccountMenuItem = ({ to, icon: Icon, label, accent, testid, onClick }) => {
  const navigate = useNavigate();
  const go = (e) => {
    e.preventDefault();
    if (onClick) { onClick(); return; }
    navigate(to);
  };
  return (
    <button
      type="button"
      onClick={go}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors ${
        accent
          ? "bg-mm-gold/20 hover:bg-mm-gold/30 text-mm-navy font-bold"
          : "text-mm-navy hover:bg-mm-parchment"
      }`}
      data-testid={testid}
    >
      <Icon size={16} strokeWidth={2.5} className={accent ? "text-mm-navy" : "text-mm-steel"} />
      <span>{label}</span>
    </button>
  );
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // "App" mode hides marketing nav and uses the account dropdown.
  // Triggered on the dashboard or any logged-in route — but Pricing should always behave normally,
  // because that's where users go to upgrade.
  const inAppMode = !!user && location.pathname !== "/" && location.pathname !== "/pricing";

  const logoTo = user ? "/dashboard" : "/";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-mm-parchment/95 backdrop-blur border-b border-mm-navy/10">
      <div className="container mx-auto flex items-center justify-between py-4">
        <Link to={logoTo} data-testid="nav-home-link" aria-label={user ? "Go to dashboard" : "Mind Map home"}>
          <Logo size={44} />
        </Link>

        {!inAppMode && (
          <nav className="hidden md:flex items-center gap-8 font-heading font-semibold text-mm-navy" data-testid="nav-marketing">
            <Link to="/" className="hover:text-mm-steel transition-colors" data-testid="nav-link-home">Home</Link>
            <Link to="/pricing" className="hover:text-mm-steel transition-colors" data-testid="nav-link-pricing">Pricing</Link>
            {user && (
              <Link
                to={user.role === "teacher" ? "/teacher/dashboard" : "/dashboard"}
                className="hover:text-mm-steel transition-colors"
                data-testid="nav-link-dashboard"
              >
                {user.role === "teacher" ? "Classes" : "Dashboard"}
              </Link>
            )}
            {user?.is_admin && (
              <Link to="/admin" className="hover:text-mm-steel transition-colors" data-testid="nav-link-admin">Admin</Link>
            )}
          </nav>
        )}

        <div className="flex items-center gap-3">
          {!inAppMode && <div className="hidden sm:block"><InstallButton compact /></div>}

          {/* Logged-out: marketing CTAs */}
          {!user && (
            <>
              <Link to="/login" className="hidden sm:inline text-mm-navy font-heading font-semibold hover:text-mm-steel" data-testid="nav-login-link">
                Continue journey
              </Link>
              <Link to="/signup" className="btn-trail btn-gold" data-testid="nav-start-free-btn">
                Start free
              </Link>
            </>
          )}

          {/* Logged-in NON-app routes (Home / Pricing): keep simple logout */}
          {user && !inAppMode && (
            <button
              className="btn-trail btn-outline-navy text-sm"
              onClick={handleLogout}
              data-testid="nav-logout-btn"
            >
              Pack up
            </button>
          )}

          {/* Logged-in IN-app: subscription badge + account popover */}
          {user && inAppMode && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full border-2 border-mm-navy/15 bg-white hover:border-mm-steel transition-colors"
                  data-testid="account-menu-trigger"
                  aria-label="Account menu"
                >
                  <span
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${planBadgeClasses(user)}`}
                    data-testid="account-plan-badge"
                  >
                    {planLabel(user)}
                  </span>
                  <span className="w-7 h-7 rounded-full bg-mm-navy text-white flex items-center justify-center" aria-hidden="true">
                    <Settings size={14} strokeWidth={2.5} />
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-64 p-2 rounded-xl border border-mm-navy/10 bg-white shadow-xl"
                data-testid="account-menu"
              >
                <div className="px-3 pt-2 pb-3 border-b border-mm-navy/10">
                  <div className="font-heading font-bold text-mm-navy truncate" data-testid="account-menu-name">{user.name}</div>
                  <div className="text-xs text-mm-navy/60 truncate">{user.email}</div>
                </div>

                <div className="mt-2 space-y-1">
                  {user.effective_plan !== "premium" && (
                    <AccountMenuItem
                      to="/pricing"
                      icon={ArrowUpCircle}
                      label="Upgrade to Summit Pass"
                      accent
                      testid="account-menu-upgrade"
                    />
                  )}
                  <AccountMenuItem to="/account" icon={User} label="My account" testid="account-menu-my-account" />
                  <AccountMenuItem to="/climbers" icon={Users} label="Climbers" testid="account-menu-climbers" />
                  <AccountMenuItem to="/badges" icon={Award} label="Scout badges" testid="account-menu-badges" />
                  <AccountMenuItem to="/billing" icon={Receipt} label="Billing" testid="account-menu-billing" />
                  {user.is_admin && (
                    <AccountMenuItem to="/admin" icon={ShieldCheck} label="Admin (HQ)" testid="account-menu-admin" />
                  )}
                </div>

                <div className="mt-2 pt-2 border-t border-mm-navy/10">
                  <AccountMenuItem
                    icon={LogOut}
                    label="Pack up & log out"
                    testid="account-menu-logout"
                    onClick={handleLogout}
                  />
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
